import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import CustomTabs from '../../components/CustomTabs';
import Select from '../../components/Select';
import Input from '../../components/input';
import { apiService } from '../../services/apiService';
import { FiUsers, FiUser, FiEye, FiMapPin, FiCalendar, FiFileText, FiClock } from 'react-icons/fi';
import '../../styles/FMCR.css';
import '../../styles/Access.css';

const initialData = [
  {
    id: 1,
    reportId: "RPT-2825-0129",
    reportTitle: "Weekly Alerts Digest",
    scope: "Available · Past week",
    generatedOn: "2026-05-15",
    assignedDriver: "Noman Aslam",
  },
  {
    id: 2,
    reportId: "RPT-2825-0128",
    reportTitle: "Weekly Fleet Performance — Active Vehicles",
    scope: "All Vehicles - Weekly",
    generatedOn: "2026-03-10",
    assignedDriver: "Waqar Siddiq",
  },
  {
    id: 3,
    reportId: "RPT-2825-0127",
    reportTitle: "Idle Activity Investigation — KHI-9034-F",
    scope: "Active Fleets · Jun 2 - Jun 9",
    generatedOn: "2026-07-01",
    assignedDriver: "Sajid Khan",
  },
  {
    id: 4,
    reportId: "RPT-2825-0126",
    reportTitle: "Alerts Summary — Last 24 Hours",
    scope: "Specific Vehicle · Last 7 days",
    generatedOn: "2026-02-28",
    assignedDriver: "Tariq Ali",
  },
  {
    id: 5,
    reportId: "RPT-2825-0125",
    reportTitle: "Driver Compliance Audit — Q2 Review",
    scope: "Available Vehicles",
    generatedOn: "2026-09-12",
    assignedDriver: "Kamran Akmal",
  },
  {
    id: 6,
    reportId: "RPT-2825-0124",
    reportTitle: "Driver Connectivity Check - All Drivers",
    scope: "Specific Trip",
    generatedOn: "2026-01-20",
    assignedDriver: "Faisal Rauf",
  },
  {
    id: 7,
    reportId: "RPT-2825-0123",
    reportTitle: "Trip Reconciliation — TRP-2024-8841",
    scope: "Specific Duration",
    generatedOn: "2026-06-18",
    assignedDriver: "Irfan Malik",
  },
  {
    id: 8,
    reportId: "RPT-2825-0122",
    reportTitle: "Fuel Consumption Analysis — May 2025",
    scope: "Specific Duration",
    generatedOn: "2026-04-22",
    assignedDriver: "Noman Aslam",
  },
  {
    id: 9,
    reportId: "RPT-2825-0121",
    reportTitle: "Hbo Activity Report",
    scope: "Active Fleets · Jun 2 - Jun 9",
    generatedOn: "2026-06-19",
    assignedDriver: "Waqar Siddiq",
  }
];

const GenerateReport = () => {
  const navigate = useNavigate();

  // Playground form states
  const [selectedScope, setSelectedScope] = useState("Active Fleets");
  const [selectedStatuses, setSelectedStatuses] = useState(["All"]);
  const [refinementVehicles, setRefinementVehicles] = useState(null);
  const [refinementAlerts, setRefinementAlerts] = useState(null);
  const [refinementDriver, setRefinementDriver] = useState(null);
  const [refinementDate, setRefinementDate] = useState(null);
  const [refinementTrip, setRefinementTrip] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Counts from vehicle status API
  const [counts, setCounts] = useState({ total: 0, total_active: 0, total_available: 0 });

  useEffect(() => {
    let active = true;
    const fetchCounts = async () => {
      try {
        const response = await apiService.getFmcrVehicleStatus();
        if (active && response && response.data) {
          setCounts(response.data);
        }
      } catch (err) {
        console.error("Error fetching FMCR vehicle counts:", err);
      }
    };
    fetchCounts();
    return () => {
      active = false;
    };
  }, []);

  const [dynamicVehicleOptions, setDynamicVehicleOptions] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [dynamicTripOptions, setDynamicTripOptions] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);

  useEffect(() => {
    let active = true;
    let typeParam = "";
    if (selectedScope === "All Vehicles") {
      typeParam = "all";
    } else if (selectedScope === "Active Fleets") {
      typeParam = "active";
    } else if (selectedScope === "Available Vehicles") {
      typeParam = "available";
    }

    // Auto-reset all selector fields when scope (tab) changes
    setRefinementVehicles(null);
    setRefinementTrip(null);
    setRefinementAlerts(null);
    setRefinementDriver(null);
    setRefinementDate(null);
    setStartDate("");
    setEndDate("");

    if (!typeParam) {
      setDynamicVehicleOptions([]);
    }

    if (selectedScope === "Specific Trip") {
      const fetchTrips = async () => {
        setTripsLoading(true);
        try {
          const response = await apiService.getFmcrTripDropdown();
          const rawTrips = response?.data || response || [];
          const formatted = (Array.isArray(rawTrips) ? rawTrips : []).map((trip) => {
            const val = trip?.value || trip?.id || trip?.trip_id || "";
            const lbl = trip?.label || trip?.trip_number || trip?.trip_id || "";
            return {
              value: String(val).trim(),
              label: String(lbl).trim()
            };
          }).filter(t => t.value && t.label);

          if (active) {
            setDynamicTripOptions([{ value: "All Trips", label: "All Trips" }, ...formatted]);
          }
        } catch (err) {
          console.error("Error fetching FMCR trips dropdown:", err);
        } finally {
          if (active) {
            setTripsLoading(false);
          }
        }
      };
      fetchTrips();
    } else {
      setDynamicTripOptions([]);
    }

    if (typeParam) {
      const fetchVehicles = async () => {
        setVehiclesLoading(true);
        try {
          const response = await apiService.getFmcrVehicleDropdown(typeParam);
          const rawVehicles = response?.data || response || [];
          const formatted = (Array.isArray(rawVehicles) ? rawVehicles : []).map((vehicle) => {
            const val = vehicle?.value || vehicle?.id || vehicle?.vehicle_id || "";
            const lbl = vehicle?.label || vehicle?.registration_number || vehicle?.name || vehicle?.title || "";
            return {
              value: String(val).trim(),
              label: String(lbl).trim()
            };
          }).filter(v => v.value && v.label);

          if (active) {
            setDynamicVehicleOptions([{ value: "All Vehicles", label: "All Vehicles" }, ...formatted]);
          }
        } catch (err) {
          console.error("Error fetching FMCR vehicles dropdown:", err);
        } finally {
          if (active) {
            setVehiclesLoading(false);
          }
        }
      };
      fetchVehicles();
    }

    return () => {
      active = false;
    };
  }, [selectedScope]);

  const countBadgeStyle = {
    background: '#ffffff',
    color: '#3d3f5c',
    borderRadius: '4px',
    border: '1px solid #e3e3e8',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '600',
    height: '18px',
    minWidth: '22px',
    padding: '0 5px',
    lineHeight: 'normal',
    boxSizing: 'border-box'
  };

  // Playground configs
  const scopesList = useMemo(() => [
    { 
      key: "All Vehicles", 
      label: (
        <>
          All Vehicles 
          <strong style={countBadgeStyle}>
            {counts.total}
          </strong>
        </>
      ), 
      icon: <FiUsers /> 
    },
    { 
      key: "Active Fleets", 
      label: (
        <>
          Active Fleets 
          <strong style={countBadgeStyle}>
            {counts.total_active}
          </strong>
        </>
      ), 
      icon: <FiUser /> 
    },
    { 
      key: "Available Vehicles", 
      label: (
        <>
          Available Vehicles 
          <strong style={countBadgeStyle}>
            {counts.total_available}
          </strong>
        </>
      ), 
      icon: <FiEye /> 
    },
    { key: "Specific Trip", label: "Specific Trip", icon: <FiMapPin /> },
    { key: "Specific Duration", label: "Specific Duration", icon: <FiCalendar /> }
  ], [counts]);

  const statusOptions = [
    { name: "All", color: "#9ca3af" },
    { name: "Available", color: "#22c55e" },
    { name: "Driver Completed", color: "#3b82f6" },
    { name: "Loading", color: "#eab308" },
    { name: "Moving", color: "#10b981" },
    { name: "Food", color: "#ef4444" },
    { name: "Resting", color: "#a855f7" },
    { name: "Unloading", color: "#f97316" },
    { name: "Way Back", color: "#6b7280" }
  ];

  const vehicleOptions = [
    { value: "All Vehicles", label: "All Vehicles" },
    { value: "KHI-9034-F", label: "KHI-9034-F" },
    { value: "LHR-1204-A", label: "LHR-1204-A" },
    { value: "KHI-5512-D", label: "KHI-5512-D" },
    { value: "ISL-7788-G", label: "ISL-7788-G" }
  ];
  const tripOptions = [
    { value: "All Trips", label: "All Trips" },
    { value: "TRP-2024-8841", label: "TRP-2024-8841" },
    { value: "TRP-2024-8842", label: "TRP-2024-8842" },
    { value: "TRP-2024-8843", label: "TRP-2024-8843" }
  ];
  const alertOptions = [
    { value: "All Alerts", label: "All Alerts" },
    { value: "Over Speeding", label: "Over Speeding" },
    { value: "Hard Braking", label: "Hard Braking" },
    { value: "Harsh Acceleration", label: "Harsh Acceleration" },
    { value: "Idle Alert", label: "Idle Alert" }
  ];
  const driverOptions = [
    { value: "All Drivers", label: "All Drivers" },
    { value: "Waqar Siddiq", label: "Waqar Siddiq" },
    { value: "Noman Aslam", label: "Noman Aslam" },
    { value: "Sajid Khan", label: "Sajid Khan" },
    { value: "Tariq Ali", label: "Tariq Ali" }
  ];
  const dateOptions = [
    { value: "Last 24 Hours", label: "Last 24 Hours" },
    { value: "Last 7 Days", label: "Last 7 Days" },
    { value: "Last 30 Days", label: "Last 30 Days" },
    { value: "Custom Range (Jun 2 - Jun 9)", label: "Custom Range (Jun 2 - Jun 9)" }
  ];

  const handleToggleStatus = (statusName) => {
    if (statusName === "All") {
      setSelectedStatuses(["All"]);
    } else {
      let updated = selectedStatuses.filter(s => s !== "All");
      if (updated.includes(statusName)) {
        updated = updated.filter(s => s !== statusName);
      } else {
        updated.push(statusName);
      }
      if (updated.length === 0) {
        updated = ["All"];
      }
      setSelectedStatuses(updated);
    }
  };

  const previewSummary = useMemo(() => {
    let duration = "";

    const activeDateLabel = refinementDate ? refinementDate.value : "Select Date";

    switch (selectedScope) {
      case "All Vehicles":
        duration = activeDateLabel === "Select Date" ? "Last 30 Days" : activeDateLabel;
        break;
      case "Active Fleets":
        duration = activeDateLabel === "Select Date" ? "Jun 2 - Jun 9" : activeDateLabel;
        break;
      case "Available Vehicles":
        duration = activeDateLabel === "Select Date" ? "Last 7 Days" : activeDateLabel;
        break;
      case "Specific Trip":
        duration = "Specific Trip";
        break;
      case "Specific Duration":
        duration = startDate && endDate ? `${startDate} to ${endDate}` : "Custom Range";
        break;
      default:
        break;
    }

    // Vehicles count:
    let finalVehicles = 0;
    if (selectedScope === "All Vehicles" || selectedScope === "Active Fleets" || selectedScope === "Available Vehicles") {
      if (refinementVehicles) {
        if (refinementVehicles.value === "All Vehicles") {
          finalVehicles = dynamicVehicleOptions.length > 1 ? (dynamicVehicleOptions.length - 1) : 0;
        } else {
          finalVehicles = 1;
        }
      }
    }

    // Trips count:
    let finalTrips = 0;
    if (selectedScope === "Specific Trip") {
      if (refinementTrip) {
        if (refinementTrip.value === "All Trips") {
          finalTrips = dynamicTripOptions.length > 1 ? (dynamicTripOptions.length - 1) : 0;
        } else {
          finalTrips = 1;
        }
      }
    }

    // Alerts count:
    let finalAlerts = 0;
    if (refinementAlerts) {
      if (refinementAlerts.value === "All Alerts") {
        finalAlerts = 4;
      } else {
        finalAlerts = 1;
      }
    }

    // Status Log count:
    let finalStatusLog = 0;
    if (selectedStatuses && selectedStatuses.includes("All")) {
      finalStatusLog = 8;
    } else if (selectedStatuses) {
      finalStatusLog = selectedStatuses.length;
    }

    return {
      vehicles: finalVehicles,
      trips: finalTrips,
      alerts: finalAlerts,
      statusLog: finalStatusLog,
      duration
    };
  }, [selectedScope, refinementVehicles, refinementTrip, refinementAlerts, selectedStatuses, refinementDate, startDate, endDate, dynamicVehicleOptions, dynamicTripOptions]);

  const isGenerateDisabled = useMemo(() => {
    if (selectedScope === "All Vehicles" || selectedScope === "Active Fleets" || selectedScope === "Available Vehicles") {
      return !refinementVehicles || !refinementAlerts;
    }
    if (selectedScope === "Specific Trip") {
      return !refinementTrip || !refinementAlerts;
    }
    if (selectedScope === "Specific Duration") {
      return !startDate || !endDate;
    }
    return true;
  }, [selectedScope, refinementVehicles, refinementAlerts, refinementTrip, startDate, endDate]);

  const handleGenerateReport = () => {
    // Navigate to /fmcr directly
    navigate("/fmcr");
  };

  return (
    <div className="fmcr_page fmcr_generate_report_page">
      <Breadcrumbs values={["FMCR", "FMCR Reports", "Generate New Report"]} />

      <div className="access_view">
        <div className="d-flex align-items-center gap-3">
          <h2>FMCR</h2>

          <div className="access_btn">
            <button 
              className="" 
              onClick={() => navigate("/fmcr", { state: { activeView: "tracking" } })}
            >
              Tracking
            </button>
            <button 
              className="active" 
              onClick={() => navigate("/fmcr")}
            >
              Reports
            </button>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            cancelBtn={() => navigate("/fmcr")}
            cancelBtnText="Cancel"
            shouldRow={false}
            isShowCancel={true}
            isShowSubmitBtn={false}
          />
        </div>
      </div>

      <div style={{ paddingLeft: '25px', paddingRight: '25px', paddingBottom: '25px' }}>
        <div className="playground_container mt-4">
        <div className="playground_header_row">
          <div className="playground_title_col">
            <h4>Report Playground</h4>
            <span>Configure report filters and see the projected summary breakdown.</span>
          </div>
        </div>

        <div className="playground_section_card">
          <h5 className="playground_section_title">Report Scope</h5>
          <p className="playground_section_subtitle">Select the vehicle or fleet scope for the generated report.</p>
          <CustomTabs
            tabs={scopesList}
            activeTab={selectedScope}
            setActiveTab={setSelectedScope}
          />
        </div>

        <div className="playground_section_card">
          <h5 className="playground_section_title">Filter By Status</h5>
          <p className="playground_section_subtitle">Filter target activity records by current trip status.</p>
          <div className="status_pills_container">
            {statusOptions.map((status, idx) => (
              <button
                key={idx}
                className={`status_pill ${selectedStatuses.includes(status.name) ? 'active' : ''}`}
                onClick={() => handleToggleStatus(status.name)}
              >
                <span className="status_pill_dot" style={{ backgroundColor: status.color }} />
                <span>{status.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="playground_section_card">
          <h5 className="playground_section_title">Refinement Selectors</h5>
          <p className="playground_section_subtitle">Apply secondary attribute constraints to target activities.</p>
          
          <div className="row mt-4">
            {(selectedScope === "All Vehicles" || selectedScope === "Active Fleets" || selectedScope === "Available Vehicles" || selectedScope === "Specific Trip") && (
              <>
                <div className="col-md-3 mb-3">
                  {selectedScope === "Specific Trip" ? (
                    <Select
                      label="Trips"
                      placeholder="Select Trip/s"
                      options={dynamicTripOptions}
                      value={refinementTrip}
                      onChange={(option) => setRefinementTrip(option)}
                      required={true}
                      showIcon={false}
                      loading={tripsLoading}
                    />
                  ) : (
                    <Select
                      label="Vehicles"
                      placeholder="Select Vehicle/s"
                      options={dynamicVehicleOptions}
                      value={refinementVehicles}
                      onChange={(option) => setRefinementVehicles(option)}
                      required={true}
                      showIcon={false}
                      loading={vehiclesLoading}
                    />
                  )}
                </div>
                <div className="col-md-3 mb-3">
                  <Select
                    label="Alerts"
                    placeholder="Select Alert/s"
                    options={alertOptions}
                    value={refinementAlerts}
                    onChange={(option) => setRefinementAlerts(option)}
                    required={true}
                    showIcon={false}
                  />
                </div>


              </>
            )}

            {selectedScope === "Specific Duration" && (
              <>
                <div className="col-md-3 mb-3">
                  <Input
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const nextStartDate = e.target.value;
                      setStartDate(nextStartDate);
                      if (endDate && nextStartDate > endDate) {
                        setEndDate("");
                      }
                    }}
                    required={true}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <Input
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      const nextEndDate = e.target.value;
                      if (startDate && nextEndDate && nextEndDate < startDate) {
                        setEndDate("");
                      } else {
                        setEndDate(nextEndDate);
                      }
                    }}
                    required={true}
                    minValue={startDate}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="playground_summary_card mt-4">
          <div className="summary_content_box">
            <div 
              className="d-flex align-items-center justify-content-center"
              style={{ 
                border: '1px solid #e3e3e8', 
                borderRadius: '8px', 
                width: '48px', 
                height: '48px', 
                background: '#ffffff',
                color: '#3d3f5c'
              }}
            >
              <FiFileText style={{ fontSize: '24px' }} />
            </div>
            <div className="summary_info_col">
              <div className="d-flex align-items-center gap-2">
                <h6 className="summary_title">Preview Report Summary</h6>
                <span style={{ fontSize: '12px', color: '#999999', fontWeight: 500 }}>
                  · {selectedScope}, {selectedStatuses.join(", ")}
                </span>
              </div>
              <div className="summary_badges_row">
                {selectedScope !== "Specific Trip" && selectedScope !== "Specific Duration" && (
                  <div className="preview_metric_badge">
                    <span>Vehicles</span>
                    <strong className="preview_metric_badge_val">{previewSummary.vehicles}</strong>
                  </div>
                )}
                {selectedScope === "Specific Trip" && (
                  <div className="preview_metric_badge">
                    <span>Trips</span>
                    <strong className="preview_metric_badge_val">{previewSummary.trips}</strong>
                  </div>
                )}
                {selectedScope === "Specific Duration" && (
                  <>
                    <div className="preview_metric_badge">
                      <span>Start Date</span>
                      <strong className="preview_metric_badge_val">{startDate || "Not Selected"}</strong>
                    </div>
                    <div className="preview_metric_badge">
                      <span>End Date</span>
                      <strong className="preview_metric_badge_val">{endDate || "Not Selected"}</strong>
                    </div>
                  </>
                )}
                {selectedScope !== "Specific Duration" && (
                  <div className="preview_metric_badge">
                    <span>Alerts</span>
                    <strong className="preview_metric_badge_val">{previewSummary.alerts}</strong>
                  </div>
                )}
                <div className="preview_metric_badge">
                  <span>Status Log</span>
                  <strong className="preview_metric_badge_val">{previewSummary.statusLog}</strong>
                </div>
              </div>
            </div>
          </div>
          
          <div className="d-flex gap-3">
            <button 
              className="details_back_btn" 
              onClick={() => {
                setSelectedScope("Active Fleets");
                setSelectedStatuses(["All"]);
                setRefinementVehicles(null);
                setRefinementAlerts(null);
                setRefinementDriver(null);
                setRefinementDate(null);
                setRefinementTrip(null);
                setStartDate("");
                setEndDate("");
              }}
            >
              Reset
            </button>
            <Button
              submitBtn={handleGenerateReport}
              submitText="Generate &rarr;"
              type="button"
              shouldRow={false}
              isShowCancel={false}
              disabled={isGenerateDisabled}
            />
        </div>
      </div>
    </div>

      {/* Idle Activity Report Locked Preview Card */}
        <div 
          className="mt-4 d-flex flex-column align-items-center justify-content-center" 
          style={{ 
            background: '#ffffff', 
            border: '1px solid #e6e6e6', 
            borderRadius: '8px', 
            padding: '80px 20px', 
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
          }}
        >
          <div 
            className="d-flex align-items-center justify-content-center mb-3" 
            style={{ 
              width: '64px', 
              height: '64px', 
              borderRadius: '50%', 
              background: '#eef2ff', 
              color: '#3b82f6', 
              fontSize: '28px' 
            }}
          >
            <FiClock />
          </div>
          <h4 style={{ fontFamily: 'Raleway', fontWeight: 700, fontSize: '18px', color: '#3d3f5c', marginBottom: '8px' }}>
            Idle Activity Report
          </h4>
          <p style={{ fontFamily: 'Roboto', fontSize: '13px', color: '#999999', maxWidth: '400px', margin: '0 auto', lineHeight: '1.5' }}>
            Your criteria is locked in. Click Generate Report above to compile and view the interactive preview.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenerateReport;
